// ===================================================================
// components/common/GameStage.tsx
// Scène de jeu fixe de 390 × 844 (taille des maquettes Claude Design),
// affichée dans la page sous l'en-tête et mise à l'échelle de la place
// disponible. Utilisée par La forge des mots et Le tisserand.
// ===================================================================
import React, { useLayoutEffect, useRef, useState } from 'react';
import './GameStage.css';

export const STAGE_W = 390;
export const STAGE_H = 844;

function useStageScale(ref: React.RefObject<HTMLDivElement>) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth, h = el.clientHeight;
      if (w > 0 && h > 0) setScale(Math.min(w / STAGE_W, h / STAGE_H));
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    return () => { ro?.disconnect(); window.removeEventListener('resize', update); };
  }, [ref]);
  return scale;
}

/** Hauteur visible sous le haut de l'élément (en-tête de l'app au-dessus). */
function useAvailableHeight(ref: React.RefObject<HTMLDivElement>) {
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      setHeight(Math.max(520, Math.round(vh - top)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [ref]);
  return height;
}

interface GameStageProps {
  /** Classe du conteneur (polices, couleurs, styles des boutons du jeu) */
  className?: string;
  children: React.ReactNode;
}

const GameStage: React.FC<GameStageProps> = ({ className, children }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const height = useAvailableHeight(rootRef);
  const scale = useStageScale(viewportRef);
  return (
    <div className={`game-stage-root${className ? ` ${className}` : ''}`} ref={rootRef} style={{ height }}>
      <div className="game-stage-viewport" ref={viewportRef}>
        <div style={{ width: STAGE_W * scale, height: STAGE_H * scale, flex: 'none' }}>
          <div className="game-stage" style={{ transform: `scale(${scale})` }}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default GameStage;
